import {
  type AgentDetails,
  type Rebate,
  type LicenceDetails,
  LicenceDetailsState,
  States,
  useGetListingAgreementConfiguration,
  useGetAgencyMembers,
  useGetTeamsForAgency,
  useInvalidateUseGetAgencyMembers,
  useInvalidateUseGetTeamsForAgency,
  useInvalidateUseGetUrbanXTeamForAgency,
  useSendInvitationEmail,
  useSoftDeleteAgent,
  useCreateAgent,
  useSetAgentDetails,
} from '@urbanx/agx.agencies.client';
import { useQueryClient } from '@tanstack/react-query';
import { flushSync } from 'react-dom';
import {
  AgxFormTextField,
  AgxFormDropdown,
  AgxFormDateField,
  saveButtonStateFor,
} from '@urbanx/agx.forms';
import {
  AgxButton,
  AgxPanel,
  AgxTab,
  AgxDropdown,
  useToast,
  AgxDivider,
  AgxSaveChangesButton,
  AgxTextArea,
  AgxToggleSwitch,
  AgxTypography,
  Icon_Arrow,
  AgxSpacer,
} from '@urbanx/agx.ui-components2';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AgencyContext } from 'contexts';
import { useSelectedAgency } from 'hooks';
import { FormPrompt } from 'components/FormPrompt';
import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation';
import LoadingState from 'components/loadingState/LoadingState';
import { normalizeEmail } from 'helpers/emailHelper';
import LogoUpload from './LogoUpload';
import AgentCrmRegistration from './agent-crm-registration/AgentCrmRegistration';
import { LicenceTypeOptions } from './LicenceTypeOptions';
import { AgentRebates } from './AgentRebates';
import { requiresAgentSaveReload } from './agentSaveWarning';
import { getErrorMessage } from 'helpers/getErrorMessage';
import {
  TeamMemberForm,
  teamMemberDefaults,
  teamMemberRequest,
  canEditLicenseeDetails,
} from './teamMemberFormDefinition';
import * as styles from './AddOrEditTeamMemberPage.css';
import * as pageStyles from '../teamEditorPage.css';
import { AgentRoleOptions } from './helpers';

const AddOrEditTeamMemberPage = ({ isEdit = false }: { isEdit?: boolean }) => {
  const { agentId } = useParams();
  const { agency, isLoading } = useSelectedAgency();
  const members = useGetAgencyMembers(agency?.id ?? '', !!agency?.id);
  const teams = useGetTeamsForAgency(agency?.id ?? '', !!agency?.id);
  const listing = useGetListingAgreementConfiguration(agency?.id ?? '', !!agency?.id);
  const [agencyContext] = useContext(AgencyContext);
  const selectedAgent = members.data?.find((agent) => agent.id === agentId);
  if (isLoading || !agency || members.isPending || teams.isPending) return <LoadingState />;
  const loadFailed = (!members.data && members.isError) || (!teams.data && teams.isError);
  if (loadFailed || (isEdit && !selectedAgent))
    return (
      <AgxTypography type="form_error" role="alert">
        {loadFailed
          ? 'Unable to load team member details. Reload the page to try again.'
          : 'Team member not found.'}
      </AgxTypography>
    );
  return (
    <TeamMemberEditor
      key={`${agency.id}-${isEdit ? agentId : 'new'}`}
      isEdit={isEdit}
      agencyId={agency.id ?? ''}
      selectedAgent={selectedAgent}
      agencyMembers={members.data ?? []}
      teams={(teams.data ?? []).flatMap((team) =>
        team.id && team.name ? [{ value: team.id, label: team.name }] : []
      )}
      licenceStates={agencyContext?.states ?? []}
      licences={agency.companyDetails?.licences}
      agencyRebates={listing.data ? (listing.data.settings.rebates ?? []) : undefined}
      listingError={listing.isError}
    />
  );
};

const TeamMemberEditor = ({
  isEdit,
  agencyId,
  selectedAgent,
  agencyMembers,
  teams,
  licenceStates,
  licences,
  agencyRebates,
  listingError,
}: {
  isEdit: boolean;
  agencyId: string;
  selectedAgent?: AgentDetails;
  agencyMembers: AgentDetails[];
  teams: { value: string; label: string }[];
  licenceStates: States[];
  licences?: LicenceDetails[];
  agencyRebates?: Rebate[];
  listingError: boolean;
}) => {
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const [activeTab, setActiveTab] = useState('agency-user-basic');
  const [createdAgentId, setCreatedAgentId] = useState<string>();
  const queryClient = useQueryClient();
  const showRebates =
    licences?.some(
      (licence) =>
        licence.state === LicenceDetailsState.QLD || licence.state === LicenceDetailsState.NSW
    ) ?? false;
  // This is the last complete server snapshot, not a second editable draft.
  const [source, setSource] = useState(selectedAgent);
  const saving = useRef(false);
  const observedSource = useRef(selectedAgent);
  useEffect(() => {
    // Save-triggered refetches must not replace the mutation's committed snapshot.
    if (observedSource.current === selectedAgent) return;
    observedSource.current = selectedAgent;
    if (!saving.current) setSource(selectedAgent);
  }, [selectedAgent]);
  const defaults = teamMemberDefaults(source, licenceStates, showRebates);
  const [saveWarning, setSaveWarning] = useState<string>();
  const [forceCheck, setForceCheck] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [emailInviteSent, setEmailInviteSent] = useState(source?.emailInvitationSent ?? false);
  const invalidateMembers = useInvalidateUseGetAgencyMembers();
  const invalidateTeams = useInvalidateUseGetTeamsForAgency();
  const invalidateUrbanXTeam = useInvalidateUseGetUrbanXTeamForAgency();
  const { createAgent } = useCreateAgent();
  const { setAgentDetails } = useSetAgentDetails();
  const form = TeamMemberForm.useForm({
    defaultValues: defaults,
    disableWhileSubmitting: true,
    onSubmit: async ({ parsedValues }) => {
      if (saving.current || saveWarning)
        return { status: 'failed', error: new Error('Reload the latest details before saving.') };
      const creating = !parsedValues.id;
      if (
        creating &&
        agencyMembers.some(
          (member) =>
            !member.softDeleted &&
            normalizeEmail(member.email) === normalizeEmail(parsedValues.email)
        )
      ) {
        return {
          status: 'rejected',
          issues: [
            { path: 'email', code: 'duplicate', message: 'User already exists', source: 'server' },
          ],
        };
      }
      saving.current = true;
      try {
        const details = { ...teamMemberRequest(parsedValues), agencyId };
        let saved: AgentDetails | undefined;
        if (creating) {
          const response = await createAgent({ ...details, id: details.email });
          await Promise.all([
            invalidateMembers(agencyId),
            invalidateTeams(agencyId),
            invalidateUrbanXTeam(agencyId, response?.agentId ?? details.email),
          ]);
          saved = queryClient
            .getQueryData<AgentDetails[]>(['getAgencyMembers', agencyId])
            ?.find((agent) => agent.id === (response?.agentId || details.email));
          if (!saved?.eTag) {
            setSaveWarning(
              'The team member was created, but the latest details could not be loaded. Your draft is preserved. Reload the page before editing this team member.'
            );
            return {
              status: 'failed',
              error: new Error('Created team member snapshot unavailable'),
            };
          }
        } else {
          saved = await setAgentDetails(details);
          if (!saved?.eTag || (details.rebateSettings && !saved.rebateSettings))
            throw new Error('The saved team member details could not be confirmed.');
          await Promise.all([
            invalidateMembers(agencyId),
            invalidateTeams(agencyId),
            invalidateUrbanXTeam(agencyId, details.id),
          ]);
        }
        setSource(saved);
        pushToast({
          type: 'success',
          message: creating
            ? `${parsedValues.firstName} ${parsedValues.lastName} has been added to the team!`
            : 'Changes saved successfully.',
        });
        if (creating) setCreatedAgentId(saved.id);
        return {
          status: 'accepted',
          baseline: teamMemberDefaults(saved, licenceStates, showRebates),
        };
      } catch (error) {
        const requiresReload = requiresAgentSaveReload(error, creating);
        const message = getErrorMessage(
          error,
          'Unable to confirm all changes were saved.',
          requiresReload
            ? 'Your draft is preserved. Reload the latest details before saving again.'
            : 'Your draft is preserved.'
        );
        if (requiresReload) setSaveWarning(message);
        else pushToast({ type: 'error', message });
        return { status: 'failed', error };
      } finally {
        // Query notifications can render after the mutation has finished. Consume the
        // refresh here so that its later render cannot replace the accepted snapshot.
        if (parsedValues.id)
          observedSource.current = queryClient
            .getQueryData<AgentDetails[]>(['getAgencyMembers', agencyId])
            ?.find((agent) => agent.id === parsedValues.id);
        saving.current = false;
      }
    },
  });
  const createdAndClean = !!createdAgentId && form.phase === 'succeeded' && !form.isDirty;
  useEffect(() => {
    // Wait for the accepted baseline to render so the unsaved-changes blocker is released.
    if (createdAndClean) navigate(`../team-members/edit/${createdAgentId}`, { replace: true });
  }, [createdAndClean, createdAgentId, navigate]);
  const values = form.values;
  const isExistingMember = isEdit || !!values.id;
  const isSaving = form.phase === 'submitting';
  const canViewLicenseeDetails = canEditLicenseeDetails(values.licenceType, licenceStates);
  const licenceOptions = LicenceTypeOptions.filter(
    (item) => !item.state || licenceStates.some((state) => String(state) === String(item.state))
  ).map((item) => item.option);
  const { sendInvitationEmail, sendInvitationEmailPending } = useSendInvitationEmail({
    onSuccess: async () => {
      setEmailInviteSent(true);
      await invalidateMembers(agencyId);
      pushToast({ type: 'success', message: 'Email Sent!' });
    },
    onError: () => pushToast({ type: 'error', message: 'Error sending email invitation' }),
  });
  const { softDeleteAgent, softDeleteAgentPending } = useSoftDeleteAgent({
    onSuccess: async () => {
      setShowDeleteConfirmation(false);
      await Promise.all([
        invalidateMembers(agencyId),
        invalidateTeams(agencyId),
        invalidateUrbanXTeam(agencyId, values.id),
      ]);
      navigate('../');
    },
    onError: () => pushToast({ type: 'error', message: 'Error saving changes' }),
  });
  const onBack = () => navigate('../team-members');
  return (
    <AgxSpacer direction="vertical" fill gap="spacing-3xl" className={pageStyles.page}>
      <AgxSpacer
        direction="horizontal"
        fill
        wrap
        gap="spacing-lg"
        contentJustification="space-between"
        contentAlignment="center"
      >
        <AgxButton
          id="return-button"
          variant="naked"
          text={`${isExistingMember ? 'Edit' : 'Add'} Agency User`}
          leftIcon={<Icon_Arrow direction="left" height={22} width={22} />}
          onClick={() => (form.isDirty ? setForceCheck(true) : onBack())}
        />
        <div className={pageStyles.actions}>
          {isExistingMember && (
            <AgxButton
              id="deleteTeamMemberButton"
              text="Delete Agency User"
              className={pageStyles.action}
              wrapperClassName={pageStyles.action}
              variant="naked"
              size="medium"
              onClick={() => setShowDeleteConfirmation(true)}
              danger
              disabled={isSaving}
            />
          )}
          <AgxSaveChangesButton
            id="save-team-member-button"
            text="Save Agency User"
            className={pageStyles.action}
            wrapperClassName={pageStyles.action}
            variant="primary"
            size="medium"
            state={saveButtonStateFor(form.phase, form.isDirty)}
            onClick={() => {
              // Reveal the profile before validation focuses an invalid field.
              flushSync(() => setActiveTab('agency-user-basic'));
              void form.submit();
            }}
            disabled={isSaving || !form.isDirty || !!saveWarning}
          />
        </div>
      </AgxSpacer>
      <AgxDivider variant="horizontal" />
      {saveWarning && (
        <AgxTypography type="form_error" role="alert">
          {saveWarning}
        </AgxTypography>
      )}
      <FormPrompt
        hasUnsavedChanges={form.isDirty}
        forceCheck={forceCheck}
        resetForceCheck={setForceCheck}
        onBack={onBack}
      />
      <div className={styles.content}>
        <AgxTab
          variant="plain"
          orientation="vertical"
          className={styles.navigation}
          options={[
            {
              id: 'agency-user-basic',
              label: 'Basic Information',
              selected: activeTab === 'agency-user-basic',
              panelId: 'agency-user-basic-panel',
            },
            ...(isExistingMember
              ? [
                  {
                    id: 'agency-user-crm',
                    label: 'CRM Integration',
                    selected: activeTab === 'agency-user-crm',
                    panelId: 'agency-user-crm-panel',
                  },
                ]
              : []),
          ]}
          onChange={(tab) => setActiveTab(tab.id)}
        />
        <div className={styles.panels}>
          <div
            role="tabpanel"
            id="agency-user-basic-panel"
            aria-labelledby="agency-user-basic"
            hidden={activeTab !== 'agency-user-basic'}
          >
            <TeamMemberForm.Root form={form} className={styles.basicForm}>
              <AgxPanel header="Agency User Details">
                <>
                  <TeamMemberForm.CustomField name="photo">
                    {(field) => (
                      <LogoUpload
                        logo={field.value}
                        onImageChange={field.onChange}
                        agencyId={agencyId}
                        isDisabled={field.disabled}
                      />
                    )}
                  </TeamMemberForm.CustomField>
                  <div className={styles.fieldPair}>
                    <TeamMemberForm.Field
                      name="firstName"
                      Input={AgxFormTextField}
                      type="text"
                      label="First name"
                      hideOptionalText
                      stretch
                    />
                    <TeamMemberForm.Field
                      name="lastName"
                      Input={AgxFormTextField}
                      type="text"
                      label="Last name"
                      hideOptionalText
                      stretch
                    />
                  </div>
                  <div className={styles.fieldPair}>
                    <TeamMemberForm.CustomField name="teams">
                      {(field) => (
                        <AgxDropdown
                          id="selTeam"
                          label="Team"
                          type="multi"
                          stretch
                          hideOptionalText
                          selected={field.value}
                          disabled={field.disabled}
                          onChange={field.onChange}
                          options={teams}
                        />
                      )}
                    </TeamMemberForm.CustomField>
                    <TeamMemberForm.Field
                      name="role"
                      Input={AgxFormDropdown}
                      options={AgentRoleOptions}
                      label="Role"
                    />
                  </div>
                  <TeamMemberForm.Field
                    name="title"
                    Input={AgxFormTextField}
                    type="text"
                    label="Job Title"
                    stretch
                  />
                  <div className={styles.fieldPair}>
                    <TeamMemberForm.Field
                      name="email"
                      Input={AgxFormTextField}
                      type="email"
                      readonly={isExistingMember}
                      label="Email"
                      hideOptionalText
                      stretch
                    />
                    <TeamMemberForm.Field
                      name="phone"
                      Input={AgxFormTextField}
                      type="text"
                      label="Phone"
                      stretch
                    />
                  </div>
                  <TeamMemberForm.Field
                    name="licenceType"
                    Input={AgxFormDropdown}
                    label="Real Estate Licence"
                    hideOptionalText
                    options={licenceOptions}
                  />
                  {canViewLicenseeDetails && (
                    <TeamMemberForm.CustomField name="useLicenseeDetails">
                      {(field) => (
                        <AgxSpacer
                          direction="horizontal"
                          gap="spacing-md"
                          contentAlignment="center"
                        >
                          <AgxToggleSwitch
                            id="switchLicenseeDetails"
                            checked={field.value}
                            disabled={field.disabled}
                            onChange={field.onChange}
                          />
                          <AgxTypography
                            htmlFor="switchLicenseeDetails"
                            id="lbllicenseeDetails"
                            type="label"
                            size="medium"
                          >
                            Licensee details
                          </AgxTypography>
                        </AgxSpacer>
                      )}
                    </TeamMemberForm.CustomField>
                  )}
                  {values.useLicenseeDetails && canViewLicenseeDetails && (
                    <>
                      <TeamMemberForm.Field
                        name="agentCompanyDetails.licenseeName"
                        Input={AgxFormTextField}
                        type="text"
                        label="Licensee name"
                        hideOptionalText
                        stretch
                      />
                      <div className={styles.fieldPair}>
                        <TeamMemberForm.Field
                          name="agentCompanyDetails.abn"
                          Input={AgxFormTextField}
                          type="abn"
                          label="ABN"
                          hideOptionalText
                          stretch
                        />
                        <TeamMemberForm.Field
                          name="agentCompanyDetails.acn"
                          Input={AgxFormTextField}
                          type="acn"
                          label="ACN"
                          hideOptionalText
                          stretch
                        />
                      </div>
                      <div className={styles.fieldPair}>
                        <TeamMemberForm.Field
                          name="licenceNumber"
                          Input={AgxFormTextField}
                          type="text"
                          label="Licence Number"
                          hideOptionalText
                          stretch
                        />
                        <TeamMemberForm.Field
                          name="expiryDate"
                          Input={AgxFormDateField}
                          variant="compact"
                          label="Expiry Date"
                          stretch
                        />
                      </div>
                    </>
                  )}
                  <AgxSpacer direction="vertical" gap="spacing-lg" fill>
                    <AgxTypography id="extrasText" type="body_ui" size="small">
                      Extras
                    </AgxTypography>
                    <AgxDivider variant="horizontal" />
                  </AgxSpacer>
                  <TeamMemberForm.CustomField
                    name="instructionsConditions"
                    focus={() => document.getElementById('instructionsConditions')?.focus()}
                  >
                    {(field) => (
                      <AgxTextArea
                        id="instructionsConditions"
                        label="Reassignment Clause"
                        defaultValue={field.value}
                        disabled={field.disabled}
                        error={field.errorText}
                        onInputValueChange={({ value }) => {
                          field.onChange(value);
                          field.onBlur();
                        }}
                        rows={6}
                        maxLength={1100}
                        hideOptionalText
                        stretch
                      />
                    )}
                  </TeamMemberForm.CustomField>
                  {isExistingMember && (
                    <AgxSpacer direction="vertical" gap="spacing-lg">
                      <AgxTypography id="emailInvitation" type="body_ui" size="small">
                        Email Invitation
                      </AgxTypography>
                      <AgxButton
                        id="sendInvitationEmailButton"
                        text={
                          sendInvitationEmailPending
                            ? 'Sending...'
                            : emailInviteSent
                              ? 'Resend Invite'
                              : 'Send Invite'
                        }
                        size="medium"
                        variant="primary"
                        disabled={sendInvitationEmailPending || emailInviteSent || isSaving}
                        onClick={() => sendInvitationEmail({ agentId: values.id })}
                      />
                    </AgxSpacer>
                  )}
                </>
              </AgxPanel>
              {showRebates && (
                <AgxSpacer direction="vertical" fill>
                  {!agencyRebates && (
                    <AgxTypography
                      type={listingError ? 'form_error' : 'body_ui'}
                      role={listingError ? 'alert' : undefined}
                    >
                      {listingError
                        ? 'Unable to load agency rebates. Reload the page to edit rebates. Profile changes can still be saved.'
                        : 'Loading agency rebates…'}
                    </AgxTypography>
                  )}
                  {agencyRebates && (
                    <AgentRebates form={form} agencyRebates={agencyRebates} licences={licences} />
                  )}
                </AgxSpacer>
              )}
            </TeamMemberForm.Root>
          </div>
          {isExistingMember && (
            <div
              role="tabpanel"
              id="agency-user-crm-panel"
              aria-labelledby="agency-user-crm"
              hidden={activeTab !== 'agency-user-crm'}
            >
              <AgentCrmRegistration agencyId={agencyId} agentId={values.id} />
            </div>
          )}
        </div>
      </div>
      <DeleteConfirmation
        name={`${values.firstName} ${values.lastName}`.trim()}
        title="Really delete user?"
        lineOne="Are you sure you want to delete user"
        lineTwo="This cannot be undone."
        primaryActionTitle="Permanently delete user"
        secondaryActionTitle="No, cancel"
        showPopUp={showDeleteConfirmation}
        disabled={softDeleteAgentPending}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={() => {
          if (!softDeleteAgentPending) softDeleteAgent({ agentId: values.id });
        }}
      />
    </AgxSpacer>
  );
};
export default AddOrEditTeamMemberPage;
